import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { industryIcon } from "@/lib/industryIcon";
import { imageUrl } from "@/lib/imageUrl";
import { EQUIPMENT_FAMILIES, visualForIndustry } from "@/data/industryVisualData";
import { industryCategorySlugs } from "@/lib/industryContent";
import "./mega-menu.css";

/**
 * Industries mega menu: a visual "featured industry" panel on the left and the industry list on the right. Hovering (or
 * focusing) a list item swaps the panel's image, title, description and equipment chips. Images/alt/description come from
 * src/data/industryVisualData.js — no image URL lives in this component; names, order and slugs come from the Industries
 * collection passed in by Header. An industry missing from the visual data falls back to its own hero image.
 * Closes on Escape or an outside click.
 */
export default function IndustriesMegaMenu({ industries = [], onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [onClose]);

  const items = useMemo(
    () =>
      [...industries]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((ind) => {
          const v = visualForIndustry(ind.slug);
          return {
            slug: ind.slug,
            name: ind.name,
            href: v?.href || `/industries/${ind.slug}`,
            image: imageUrl(v?.image || ind.hero_image || ind.image),
            alt: v?.alt || ind.image_alt || ind.name,
            description: v?.description || ind.short_description || "",
            equipment: industryCategorySlugs(ind).map((slug) => (EQUIPMENT_FAMILIES[slug] ? { slug, ...EQUIPMENT_FAMILIES[slug] } : null)).filter(Boolean).slice(0, 3),
            source: ind,
          };
        }),
    [industries],
  );

  const [activeSlug, setActiveSlug] = useState(null);
  const current = items.find((i) => i.slug === activeSlug) || items[0];

  // Warm the cache shortly after opening so hovering through the list never waits on an image.
  useEffect(() => {
    const t = setTimeout(() => items.forEach((i) => { if (i.image) { const im = new Image(); im.src = i.image; } }), 350);
    return () => clearTimeout(t);
  }, [items]);

  if (!items.length) return null;

  return (
    <nav aria-label="Industries" data-testid="industries-mega-menu" className="absolute left-0 right-0 top-full z-50 flex justify-center px-4 pt-2">
      <div
        ref={ref}
        className="mm-drop grid w-full max-w-[1180px] overflow-hidden rounded-2xl border border-[#E4E9F2] bg-white shadow-[0_30px_70px_-30px_rgba(7,27,58,0.5)] lg:grid-cols-[300px_1fr] xl:grid-cols-[minmax(340px,37%)_1fr]"
      >
        <FeaturedPanel item={current} onClose={onClose} />

        <div className="p-5 lg:p-7">
          <div className="mb-4 flex items-end justify-between gap-4 border-b border-[#E4E9F2] pb-4">
            <div>
              <div className="mb-1.5 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1769FF]"><span className="h-px w-6 bg-[#1769FF]" aria-hidden="true" />Industries</div>
              <div className="font-heading text-[22px] font-semibold leading-tight tracking-[-0.02em] text-[#13233F]">Explore by Industry</div>
            </div>
            <Link to="/industries" onClick={onClose} className="group inline-flex shrink-0 items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-[#1769FF] hover:text-[#0F5CE6]">
              All industries
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>

          <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5" onMouseLeave={() => setActiveSlug(null)}>
            {items.map((it) => {
              const Icon = industryIcon(it.source);
              const on = it.slug === current?.slug;
              return (
                <li key={it.slug}>
                  <Link
                    to={it.href}
                    onClick={onClose}
                    onMouseEnter={() => setActiveSlug(it.slug)}
                    onFocus={() => setActiveSlug(it.slug)}
                    data-testid={`industries-menu-item-${it.slug}`}
                    aria-current={on ? "true" : undefined}
                    className={`group relative flex items-center gap-3 rounded-xl px-3 py-2 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1769FF] ${on ? "bg-[#EEF4FF]" : "hover:bg-[#F6F8FC]"}`}
                  >
                    <span className={`absolute inset-y-2 left-0 w-[3px] rounded-full bg-[#1769FF] transition-opacity duration-200 ${on ? "opacity-100" : "opacity-0"}`} aria-hidden="true" />
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 ${on ? "bg-[#1769FF] text-white" : "bg-[#F1F4F9] text-[#1769FF]"}`}><Icon className="h-[18px] w-[18px]" strokeWidth={1.7} aria-hidden="true" /></span>
                    <span className={`line-clamp-2 min-w-0 flex-1 text-[14px] leading-snug ${on ? "font-semibold text-[#0B2348]" : "font-medium text-[#13233F]"}`}>{it.name}</span>
                    <ArrowUpRight className={`h-4 w-4 shrink-0 transition-all duration-200 ${on ? "translate-x-0 text-[#1769FF]" : "-translate-x-1 text-transparent"}`} aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}

/**
 * The featured-industry panel. The photo cross-fades: the previous image stays underneath while the new one animates in,
 * then the old layer is dropped. The title/description/equipment block re-keys per industry so it slides in with it.
 */
function FeaturedPanel({ item, onClose }) {
  const [layers, setLayers] = useState(() => (item ? [item] : []));
  useEffect(() => {
    if (!item) return undefined;
    setLayers((l) => (l[l.length - 1]?.slug === item.slug ? l : [...l.slice(-1), item]));
    const t = setTimeout(() => setLayers([item]), 700);
    return () => clearTimeout(t);
  }, [item]);

  if (!item) return null;
  return (
    <div className="relative hidden min-h-[470px] overflow-hidden bg-[#071B3A] text-white lg:block">
      {layers.map((l, i) => (
        <img
          key={l.slug}
          src={l.image}
          alt={l.alt}
          width="810"
          height="900"
          decoding="async"
          draggable="false"
          className={`absolute inset-0 h-full w-full object-cover ${i === layers.length - 1 && layers.length > 1 ? "mm-img-in" : ""}`}
        />
      ))}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,27,58,0.35)_0%,rgba(7,27,58,0)_30%,rgba(7,27,58,0.72)_62%,#071B3A_100%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#1769FF]/25 blur-[80px]" aria-hidden="true" />

      <div className="relative flex h-full min-h-[470px] flex-col justify-end p-7">
        <div key={item.slug} className="mm-text-in">
          <div className="mb-3 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7FB0FF]"><span className="h-px w-6 bg-[#7FB0FF]" aria-hidden="true" />Featured industry</div>
          <div className="font-heading text-[26px] font-semibold leading-[1.1] tracking-[-0.02em]">{item.name}</div>
          {item.description && <p className="mt-2.5 text-[14px] leading-relaxed text-white/75">{item.description}</p>}

          {item.equipment.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">Shingare equipment</div>
              <ul className="flex gap-2.5">
                {item.equipment.map((e) => (
                  <li key={e.slug} className="w-[72px] text-center">
                    <span className="mx-auto flex h-[46px] w-[46px] items-center justify-center overflow-hidden rounded-xl bg-white shadow-[0_6px_16px_-6px_rgba(0,0,0,0.5)]"><img src={e.image} alt="" width="46" height="46" loading="lazy" decoding="async" className="h-full w-full object-contain" /></span>
                    <span className="mt-1.5 block text-[10.5px] leading-tight text-white/70">{e.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Link to={item.href} onClick={onClose} className="group mt-5 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.1em] text-white">
            <span className="border-b border-white/35 pb-0.5 transition-colors group-hover:border-white">Explore industry</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}
